import { REFERRAL_TIERS, BONUSES } from '@/lib/affiliate';

/**
 * Public-facing summary of the GRID Affiliate Referral Program — what you can
 * earn and how. The two cards are exported individually so pages can lay them
 * out side by side; `HowYouEarn` renders them stacked for the simple case.
 */
export function HowYouEarnCard() {
  return (
    <div className="terminal-card">
      <div className="card-header">
        <span className="card-title">HOW YOU EARN</span>
      </div>
      <div className="card-body">
        <p className="prose prose--muted">
          GRID pays you for helping bring in new Web2 and Web3 business — websites, applications, automation,
          audits, blockchain projects, and ongoing support. Commission is a percentage of the project revenue
          GRID actually collects, paid after the client pays. There is no payout cap.
        </p>
        <div className="table-wrap" style={{ border: 'none' }}>
          <table className="terminal-table table-readable">
            <thead>
              <tr>
                <th>REFERRAL TYPE</th>
                <th>WHAT IT MEANS</th>
                <th style={{ textAlign: 'right' }}>COMMISSION</th>
              </tr>
            </thead>
            <tbody>
              {REFERRAL_TIERS.map((t) => (
                <tr key={t.key}>
                  <td className="cell-tight" style={{ color: 'var(--gold)', fontWeight: 700 }}>{t.label}</td>
                  <td style={{ color: 'var(--muted)' }}>{t.blurb}</td>
                  <td className="cell-tight" style={{ textAlign: 'right', color: 'var(--success)', fontWeight: 700 }}>
                    {t.rate}%{t.recurring ? ' / mo' : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul className="rules-list">
          <li><strong style={{ color: 'var(--gold)' }}>GRID never cold-calls.</strong> Only refer people who have agreed to be contacted or who reach out themselves — you must be able to show proof of consent if asked.</li>
          <li><strong>One-time work:</strong> your commission is the single highest referral tier your involvement reaches — Warm Introduction, Qualified Referral, and Active Closing Partner do not stack.</li>
          <li><strong>Recurring work is separate:</strong> 5% of each month GRID collects on an approved ongoing plan, for up to 12 months, in addition to any one-time commission.</li>
          <li>Paid only after GRID receives the client's payment, on collected revenue (not quotes), within 30 days of collection. No minimum payout.</li>
          <li>Paid in RLUSD — a US-dollar stablecoin on the XRP Ledger — to your XRPL wallet, matching your USD commission at the time GRID processes the payout. Your wallet must have an RLUSD trustline to receive payment.</li>
          <li>Commission excludes taxes, processing fees, software/licenses, hosting and subcontractor pass-through, ad spend, gas/network fees, refunds, and third-party costs.</li>
          <li>If a client is refunded or charges back, no commission is owed on that amount; anything already paid may be offset against future commissions.</li>
          <li>If you promote GRID publicly (posts, videos, DMs, newsletters), clearly disclose you may earn a commission.</li>
          <li>Affiliates are independent contractors and may need to complete tax forms (W-9 / W-8) before payment.</li>
        </ul>

        <div className="field-label" style={{ marginTop: '18px', marginBottom: '8px' }}>Bonuses</div>
        <ul className="rules-list" style={{ marginTop: 0 }}>
          {BONUSES.map((b) => (
            <li key={b.label}><strong style={{ color: 'var(--gold)' }}>{b.label}:</strong> {b.desc}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function TierMeaningsCard() {
  return (
    <div className="terminal-card">
      <div className="card-header">
        <span className="card-title">WHAT EACH TIER MEANS</span>
      </div>
      <div className="card-body" style={{ gap: '16px' }}>
        {REFERRAL_TIERS.map((t) => (
          <div key={t.key}>
            <div style={{ color: 'var(--gold)', fontWeight: 700, fontSize: '12px', letterSpacing: '0.06em' }}>
              {t.label} — {t.rate}%{t.recurring ? ' / MO (UP TO 12)' : ''}
            </div>
            <p className="prose prose--sm" style={{ margin: '5px 0 0' }}>
              <strong style={{ color: 'var(--success)' }}>Counts: </strong>
              <span style={{ color: 'var(--muted)' }}>{t.counts}</span>
            </p>
            <p className="prose prose--sm" style={{ margin: '3px 0 0' }}>
              <strong style={{ color: 'var(--danger)' }}>Doesn't count: </strong>
              <span style={{ color: 'var(--muted)' }}>{t.notCounts}</span>
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function HowYouEarn({ compact = false }: { compact?: boolean }) {
  return (
    <>
      <HowYouEarnCard />
      {!compact && <TierMeaningsCard />}
    </>
  );
}
